'use client';

import { useEffect, useRef, useState } from 'react';
import type { ConnectionStatus, StreamStats } from './DroneStreamPlayer';

interface StreamMetricsHUDProps {
  status: ConnectionStatus;
  stats: StreamStats | null;
  /** `overlay` = compact strip for on-video use; `panel` = full card grid. */
  variant?: 'overlay' | 'panel';
}

const STATUS_STYLES: Record<ConnectionStatus, { label: string; cls: string }> = {
  connecting: { label: 'Connecting', cls: 'bg-amber-500/15 text-amber-300 ring-amber-400/40 shadow-amber-500/30' },
  connected: { label: 'Connected', cls: 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/40 shadow-emerald-500/30' },
  reconnecting: { label: 'Reconnecting', cls: 'bg-orange-500/15 text-orange-300 ring-orange-400/40 shadow-orange-500/30' },
  error: { label: 'Error', cls: 'bg-red-500/15 text-red-300 ring-red-400/40 shadow-red-500/30' },
};

export function StatusBadge({ status }: { status: ConnectionStatus }) {
  const s = STATUS_STYLES[status];
  const pulse = status === 'connecting' || status === 'reconnecting';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider ring-1 shadow-[0_0_12px] ${s.cls}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full bg-current ${pulse ? 'animate-pulse' : ''}`} />
      {s.label}
    </span>
  );
}

function pad(n: number, w: number) {
  return n.toString().padStart(w, '0');
}

/** Millisecond wall clock driven by requestAnimationFrame for glass-to-glass comparison. */
export function LiveClock({ className = '' }: { className?: string }) {
  const [text, setText] = useState('--:--:--.---');
  const raf = useRef<number>(0);

  useEffect(() => {
    const tick = () => {
      const d = new Date();
      setText(
        `${pad(d.getHours(), 2)}:${pad(d.getMinutes(), 2)}:${pad(d.getSeconds(), 2)}.${pad(d.getMilliseconds(), 3)}`,
      );
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, []);

  return <span className={`font-mono tabular-nums ${className}`}>{text}</span>;
}

function fmt(v: number | undefined, digits = 1) {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toFixed(digits);
}

interface MetricProps {
  label: string;
  value: string;
  unit?: string;
  tone?: 'default' | 'good' | 'warn' | 'bad';
}

const TONE: Record<NonNullable<MetricProps['tone']>, string> = {
  default: 'text-slate-100',
  good: 'text-emerald-300',
  warn: 'text-amber-300',
  bad: 'text-red-300',
};

function MetricCard({ label, value, unit, tone = 'default' }: MetricProps) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/70 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 font-mono text-xl tabular-nums ${TONE[tone]}`}>
        {value}
        {unit && <span className="ml-1 text-xs text-slate-500">{unit}</span>}
      </div>
    </div>
  );
}

function latencyTone(ms: number | undefined, warn: number, bad: number): MetricProps['tone'] {
  if (ms == null) return 'default';
  if (ms >= bad) return 'bad';
  if (ms >= warn) return 'warn';
  return 'good';
}

export default function StreamMetricsHUD({ status, stats, variant = 'panel' }: StreamMetricsHUDProps) {
  if (variant === 'overlay') {
    return (
      <div className="pointer-events-none flex flex-wrap items-center gap-2 text-xs">
        <StatusBadge status={status} />
        <span className="rounded bg-black/60 px-2 py-0.5 font-mono text-slate-200 ring-1 ring-white/10">
          JB {fmt(stats?.jitterBufferDelayMs, 0)}ms · DEC {fmt(stats?.decodeTimeMs, 1)}ms · RTT {fmt(stats?.rttMs, 0)}ms
        </span>
        <span className="rounded bg-black/60 px-2 py-0.5 font-mono text-slate-200 ring-1 ring-white/10">
          {fmt(stats?.fps, 0)} fps · {fmt(stats?.bitrateMbps, 2)} Mbps
          {stats && stats.width > 0 ? ` · ${stats.width}×${stats.height}` : ''}
        </span>
        <span className="rounded bg-cyan-500/15 px-2 py-0.5 text-cyan-200 ring-1 ring-cyan-400/30">
          <LiveClock />
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-500">Connection</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div className="rounded-lg border border-slate-800 bg-slate-900/70 p-3">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Status</div>
            <div className="mt-2">
              <StatusBadge status={status} />
            </div>
          </div>
          <div className="rounded-lg border border-slate-800 bg-slate-900/70 p-3 sm:col-span-2">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
              Glass-to-Glass Clock
            </div>
            <LiveClock className="mt-1 block text-xl text-cyan-200" />
          </div>
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-500">Latency &amp; Playout</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <MetricCard
            label="Jitter Buffer"
            value={fmt(stats?.jitterBufferDelayMs, 1)}
            unit="ms"
            tone={latencyTone(stats?.jitterBufferDelayMs, 60, 150)}
          />
          <MetricCard
            label="Decode Time"
            value={fmt(stats?.decodeTimeMs, 2)}
            unit="ms"
            tone={latencyTone(stats?.decodeTimeMs, 8, 16)}
          />
          <MetricCard label="RTT" value={fmt(stats?.rttMs, 1)} unit="ms" tone={latencyTone(stats?.rttMs, 50, 120)} />
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-500">Stream Health</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <MetricCard label="FPS" value={fmt(stats?.fps, 0)} />
          <MetricCard label="Bitrate" value={fmt(stats?.bitrateMbps, 2)} unit="Mbps" />
          <MetricCard
            label="Resolution"
            value={stats && stats.width > 0 ? `${stats.width}×${stats.height}` : '—'}
          />
          <MetricCard
            label="Packets Lost"
            value={stats ? String(stats.packetsLost) : '—'}
            tone={stats && stats.packetsLost > 0 ? 'warn' : 'default'}
          />
          <MetricCard
            label="Frames Dropped"
            value={stats ? String(stats.framesDropped) : '—'}
            tone={stats && stats.framesDropped > 0 ? 'warn' : 'default'}
          />
          <MetricCard
            label="Buffer Ahead"
            value={fmt(stats ? stats.bufferAheadSec * 1000 : undefined, 0)}
            unit="ms"
            tone={latencyTone(stats ? stats.bufferAheadSec * 1000 : undefined, 100, 250)}
          />
        </div>
      </section>
    </div>
  );
}
